import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { ApiResponse, MarketPrices, TradingStatus } from '../types'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
})

let isRefreshing = false
let failedQueue: Array<{
  resolve: (value: unknown) => void
  reject: (reason: unknown) => void
}> = []

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const accessToken = localStorage.getItem('accessToken')
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`
            }
            return api(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (!refreshToken) throw new Error('No refresh token')

        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken }, {
          withCredentials: true
        })

        const { accessToken } = response.data.data
        localStorage.setItem('accessToken', accessToken)

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
        }

        processQueue(null, accessToken)
        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export const setAuthTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem('accessToken', accessToken)
  localStorage.setItem('refreshToken', refreshToken)
}

export const clearAuthTokens = () => {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
}

export const getAccessToken = () => localStorage.getItem('accessToken')
export const getRefreshToken = () => localStorage.getItem('refreshToken')

// ---------- Auth ----------
export const authApi = {
  register: (data: { email: string; password: string; firstName: string; lastName: string; phone?: string }) =>
    api.post<ApiResponse<{ user: any; tokens: { accessToken: string; refreshToken: string } }>>('/auth/register', data),

  login: (data: { email: string; password: string }) =>
    api.post<ApiResponse<{ user: any; accessToken: string; refreshToken?: string }>>('/auth/login', data),

  // ورود با لینک اختصاصی که مدیر برای کاربر ساخته است
  loginWithLink: (token: string) =>
    api.post<ApiResponse<{ user: any; accessToken: string; refreshToken?: string }>>('/auth/login-link', { token }),

  logout: () =>
    api.post<ApiResponse<null>>('/auth/logout'),

  getProfile: () =>
    api.get<ApiResponse<any>>('/auth/profile'),

  updateProfile: (data: { firstName?: string; lastName?: string; phone?: string }) =>
    api.put<ApiResponse<any>>('/auth/profile', data),

  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.put<ApiResponse<null>>('/auth/change-password', data)
}

// ---------- Products ----------
export const productsApi = {
  getAll: (params?: {
    type?: 'GOLD' | 'SILVER'
    category?: 'SHMESH' | 'ABSHADE' | 'COIN' | 'BAR'
    search?: string
    page?: number
    limit?: number
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }) => api.get<ApiResponse<{ products: any[]; pagination: any }>>('/products', { params }),

  getCategories: () =>
    api.get<ApiResponse<any[]>>('/products/categories'),

  getById: (id: string) =>
    api.get<ApiResponse<any>>(`/products/${id}`),

  getBySlug: (slug: string) =>
    api.get<ApiResponse<any>>(`/products/slug/${slug}`),

  getPriceHistory: (id: string, days?: number) =>
    api.get<ApiResponse<any[]>>(`/products/${id}/price-history`, { params: { days } })
}

// ---------- Orders ----------
export const ordersApi = {
  create: (data: {
    type: 'BUY' | 'SELL'
    items: Array<{ productId: string; quantity: number }>
    shippingAddress?: any
    notes?: string
  }) => api.post<ApiResponse<any>>('/orders', data),

  getMyOrders: (params?: {
    status?: string
    type?: string
    page?: number
    limit?: number
  }) => api.get<ApiResponse<{ orders: any[]; pagination: any }>>('/orders', { params }),

  getById: (id: string) =>
    api.get<ApiResponse<any>>(`/orders/${id}`),

  cancel: (id: string) =>
    api.delete<ApiResponse<null>>(`/orders/${id}`)
}

// ---------- Prices (قیمت خام طلا و نقره) ----------
export const pricesApi = {
  getCurrent: () =>
    api.get<ApiResponse<MarketPrices>>('/prices/current')
}

// ---------- وضعیت معاملات (استاپ خرید/فروش توسط مدیر) ----------
export const tradingApi = {
  getStatus: () => api.get<ApiResponse<TradingStatus>>('/trading/status')
}

export default api